import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Firestore } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Functions } from '@angular/fire/functions';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { AuthStore } from '@core/services/auth/auth.store';
import { FirestoreApi } from '@core/services/data/firestore-api.service';
import { ProductAnalyticsService } from '@core/services/infra/product-analytics.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, filter, map, of, startWith, switchMap, tap } from 'rxjs';

import {
  ActionCard,
  CardEntities,
  CardIntent,
  CardLinkage,
  CardPriority,
  CardSource,
  CockpitFilters,
  CockpitMember,
  CockpitTask,
  CockpitTaskOutcome,
  LinkageRef,
  ProposedEffect} from '@shared/definitions/action-card.model';
import {
  DEFAULT_FILTERS,
  getCardEffects,
  matchesAssigneeFilter,
  TERMINAL_CARD_STATUSES,
  TERMINAL_CARD_STATUS_ARRAY
} from '@shared/definitions/action-card.model';
import { dueDateToMillis } from '@app/pages/apps/cockpit/services/cockpit-task-utils';
import type { AiRouterRequest, SummarizeProgressResponse, SummarizeProgressResult } from '@app/pages/apps/cockpit/services/card-progress-summary.service';
import { matchesCardSearch } from '@app/pages/apps/cockpit/services/cockpit-search.util';
import {
  ApproveOptions,
  ApproveResult,
  ApproveSendMode,
  BulkCardAction,
  BulkCardResult,
  ICockpitStore,
  ARCHIVE_PAGE_SIZE,
  type CockpitRolling30
} from './cockpit-store.interface';
import { CockpitGroup, buildInProgressGroups } from '@app/pages/apps/cockpit/services/cockpit-group.util';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';
import {
  clearPersistedEffectsEnabled,
  hydrateEffectsEnabled,
  persistEffectsEnabled,
  prunePersistedEffectsEnabled,
} from './cockpit-effects-persistence';

// Terminal card statuses (`TERMINAL_CARD_STATUSES` / `TERMINAL_CARD_STATUS_ARRAY`)
// now live in `@shared/definitions/action-card.model` so the inbox store scopes
// its listener to the exact same set. `failed` is included so a failed action
// never silently disappears (retry stays available from the detail drawer);
// this set is also what `activeWorkflowCards` EXCLUDES to avoid double-counting
// a card that resolved outside the normal workflow-completion path.

// Page size for the lazily-loaded archive view now lives on the store contract
// (`ARCHIVE_PAGE_SIZE`, imported below): the feed quotes the bound to the owner,
// so it must not be a private number here (#257).

/** Local start-of-today as an ISO string, for the resolved-today query bound. */
function startOfTodayIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * Phase 2 Firestore-backed implementation of the Cockpit store.
 *
 * Subscribes to `workspaces/{currentWorkspaceId}/actionCards` as a realtime
 * stream and exposes a signal-based API (`ICockpitStore`). Writes are
 * restricted to status and resolution fields — Firestore rules reject
 * anything else. Cloud Functions are the sole creators of Action Cards.
 */
@Injectable()
export class CockpitFirestoreStore implements ICockpitStore {
  private readonly firestore = inject(Firestore);
  private readonly fs = inject(FirestoreApi);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);
  private readonly authStore = inject(AuthStore);
  private readonly productAnalytics = inject(ProductAnalyticsService);

  /**
   * Uid of the signed-in user. Read from `AuthStore` (a signal) rather than
   * `auth.currentUser` (a plain property) so the "my cards" filter recomputes
   * when auth resolves instead of freezing on the pre-hydration `null`.
   */
  readonly currentUserId = computed<string | null>(
    () => this.authStore.user()?.userUID ?? null
  );

  /**
   * Members of the active workspace, projected from the workspace document the
   * `WorkspaceStore` already holds — no extra read. Same projection as
   * `workspace-info.component.ts`'s `getWorkspaceMembers()`. Sorted by display
   * name so the picker order is stable between renders.
   */
  readonly workspaceMembers = computed<readonly CockpitMember[]>(() => {
    const members = this.workspaceStore.currentWorkspace()?.members ?? {};
    return Object.entries(members)
      .filter(([userId]) => !!userId)
      .map(([userId, m]) => ({
        userId,
        displayName: (m?.displayName || m?.email || userId).trim(),
        email: m?.email ?? '',
        role: m?.role ?? 'standard'
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  });

  /**
   * Active working-set listener: non-terminal cards only. The archive (terminal
   * cards) loads on demand via `loadArchive()`. No fallback to the full
   * collection: a query error surfaces so the bounded read can be validated (it
   * must not silently revert to streaming the whole actionCards collection).
   */
  private readonly activeCards$: Observable<ActionCard[]> = toObservable(this.workspaceStore.currentWorkspaceId).pipe(
    filter((wid): wid is string => !!wid),
    switchMap((wid) => {
      const ref = this.fs.collection(this.firestore, `workspaces/${wid}/actionCards`);
      const q = this.fs.query(ref, this.fs.where('status', 'not-in', TERMINAL_CARD_STATUS_ARRAY));
      return this.fs.collectionData(q, { idField: 'id' }) as Observable<ActionCard[]>;
    }),
    // Defensive normalization: a malformed card (missing extraction/entities,
    // proposal, language…) must never crash the feed/dashboard rendering. One
    // bad doc otherwise takes down the whole cockpit on load.
    map((cards) => cards.map(normalizeCard)),
    // Audit #83 point 65 — the first snapshot of the working set is also the
    // only reliable list of cards a persisted checklist can still belong to.
    // Everything else in localStorage is a card that resolved, was rejected or
    // no longer exists. Once per workspace switch, not per emission: re-running
    // it on every snapshot would race the write for the card being edited.
    tap({
      next: (cards) => {
        if (this.prunedWorkspaces.has(this.workspaceStore.currentWorkspaceId() ?? '')) return;
        this.prunedWorkspaces.add(this.workspaceStore.currentWorkspaceId() ?? '');
        prunePersistedEffectsEnabled(new Set(cards.map((c) => c.id)));
      },
    }),
  );

  /**
   * Workspaces whose stale checklists have already been pruned this session.
   * The prune must run once per workspace, on its first snapshot — see the
   * `tap` above.
   */
  private readonly prunedWorkspaces = new Set<string>();

  /**
   * Cards resolved TODAY (terminal) — keeps `resolvedToday` and the
   * post-approve "résolu aujourd'hui" UX live without loading the whole
   * archive. No fallback: a query error surfaces (validate the scoped read).
   */
  private readonly recentResolved$: Observable<ActionCard[]> = toObservable(this.workspaceStore.currentWorkspaceId).pipe(
    filter((wid): wid is string => !!wid),
    switchMap((wid) => {
      const ref = this.fs.collection(this.firestore, `workspaces/${wid}/actionCards`);
      const q = this.fs.query(ref, this.fs.where('resolution.resolvedAt', '>=', startOfTodayIso()));
      return (this.fs.collectionData(q, { idField: 'id' }) as Observable<ActionCard[]>).pipe(
        map((cards) => cards.map(normalizeCard)),
      );
    }),
  );

  private readonly _activeCards = toSignal(this.activeCards$, { initialValue: [] as ActionCard[] });
  private readonly _recentResolved = toSignal(this.recentResolved$, { initialValue: [] as ActionCard[] });
  /** Lazily-loaded archive (terminal cards) — populated by `loadArchive()`. */
  private readonly _archiveCards = signal<readonly ActionCard[]>([]);
  /** All-time terminal count from a `count()` aggregate (bounded mode). */
  private readonly _archivedTotal = signal(0);
  /** Flipped by a successful `loadArchive()` — see `ICockpitStore.archiveLoaded`. */
  private readonly _archiveLoaded = signal(false);
  readonly archiveLoaded = this._archiveLoaded.asReadonly();

  /**
   * Merged realtime view: active working-set + today's resolved + any loaded
   * archive, de-duplicated by id (a card resolved today appears in both the
   * today stream and, once loaded, the archive). In legacy mode `_activeCards`
   * already holds everything, so the others are redundant (dedup is a no-op).
   */
  private readonly _remoteCards = computed<ActionCard[]>(() => {
    const byId = new Map<string, ActionCard>();
    for (const c of this._activeCards()) byId.set(c.id, c);
    for (const c of this._recentResolved()) byId.set(c.id, c);
    for (const c of this._archiveCards()) byId.set(c.id, c);
    return [...byId.values()];
  });

  /**
   * Realtime stream of the OPEN `workspaces/{wid}/cockpitTasks` — owner-side
   * follow-ups, calendar appointments, transfer trackers written by the
   * `CREATE_TASK` executor. Empty until a workflow first emits one.
   *
   * Filtered to `status == 'open'` at the query level: only `openTasks` ever
   * consumes this stream, while done/cancelled tasks accumulate for the life of
   * the workspace. A single equality filter needs no composite index and keeps
   * the page-load read cost proportional to the actionable set, not history.
   */
  private readonly tasks$: Observable<CockpitTask[] | null> = toObservable(
    this.workspaceStore.currentWorkspaceId,
  ).pipe(
    switchMap((wid) => {
      // No workspace resolved yet — there is nothing to wait FOR, so publish an
      // empty (not a pending) result. Otherwise the skeleton would spin forever
      // on a session that never lands on a workspace.
      if (!wid) return of<CockpitTask[]>([]);
      const ref = this.fs.collection(this.firestore, `workspaces/${wid}/cockpitTasks`);
      const q = this.fs.query(ref, this.fs.where('status', '==', 'open'));
      // `null` = "the first snapshot has not arrived". Emitted again on every
      // workspace switch so the task surface shows a skeleton rather than the
      // previous workspace's list or a false "nothing to do" (DESIGN.md §13.3).
      return (this.fs.collectionData(q, { idField: 'id' }) as Observable<CockpitTask[]>).pipe(
        map((tasks) => tasks as CockpitTask[] | null),
        startWith(null),
      );
    })
  );

  /** Raw stream state: `null` while the first snapshot is in flight. */
  private readonly _tasksSnapshot = toSignal<CockpitTask[] | null>(this.tasks$, {
    initialValue: null,
  });

  private readonly _cockpitTasks = computed<CockpitTask[]>(() => this._tasksSnapshot() ?? []);

  /**
   * Per-card optimistic patches kept on top of the realtime stream.
   * Owner-driven mutations (linkage edits, etc.) write here synchronously
   * so the UI updates instantly; the patch is dropped once the realtime
   * subscription reflects the same state, or on callable failure.
   * Without this, every linkage tweak felt slow: the chip stayed live until
   * the callable round-trip + Firestore propagation completed.
   */
  private readonly _optimisticPatches = signal<Record<string, OptimisticPatch>>(
    // V2.7 — restore per-card effect toggles persisted on the last visit so the
    // owner's checklist survives a reload (dropped on approve).
    hydrateEffectsEnabled(),
  );

  /**
   * Merged view: realtime cards layered with any pending optimistic
   * patches. All downstream computeds read from this — they were already
   * accessing `this._cards()`, so the overlay stays transparent to them.
   */
  private readonly _cards = computed<ActionCard[]>(() => {
    const remote = this._remoteCards();
    const patches = this._optimisticPatches();
    if (Object.keys(patches).length === 0) return remote;
    return remote.map((card) => {
      const patch = patches[card.id];
      if (!patch) return card;
      return applyOptimisticPatch(card, patch);
    });
  });

  private readonly _selectedId = signal<string | null>(null);
  private readonly _filters = signal<CockpitFilters>({ ...DEFAULT_FILTERS });

  readonly cards = this._cards;
  readonly selectedId = this._selectedId.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly cockpitTasks = this._cockpitTasks;
  readonly tasksLoading = computed(() => this._tasksSnapshot() === null);
  readonly openTasks = computed<readonly CockpitTask[]>(() =>
    this._cockpitTasks()
      .filter((t) => t.status === 'open')
      .slice()
      .sort((a, b) => dueDateToMillis(a) - dueDateToMillis(b))
  );

  readonly filteredCards = computed(() => {
    const all = this._cards();
    const f = this._filters();
    const allowedIds = f.cardIds && f.cardIds.length > 0 ? new Set(f.cardIds) : null;

    return all
      .filter((c) => (allowedIds ? allowedIds.has(c.id) : true))
      .filter((c) => this.matchesView(c, f.savedView))
      .filter((c) => (f.sources.length === 0 ? true : f.sources.includes(c.source)))
      .filter((c) => (f.intents.length === 0 ? true : f.intents.includes(c.classification.intent)))
      .filter((c) => (f.priorities.length === 0 ? true : f.priorities.includes(c.classification.priority)))
      .filter((c) => (f.statuses.length === 0 ? true : f.statuses.includes(c.status)))
      .filter((c) => matchesAssigneeFilter(c, f.assignee, this.currentUserId()))
      .filter((c) => this.matchesSearch(c, f.searchText))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  });

  readonly selectedCard = computed<ActionCard | null>(() => {
    const id = this._selectedId();
    return this._cards().find((c) => c.id === id) ?? null;
  });

  readonly counts = computed(() => {
    const all = this._cards();
    return {
      pending: all.filter((c) => c.status === 'pending').length,
      executed: all.filter((c) => c.status === 'executed').length,
      approved: all.filter((c) => c.status === 'approved').length,
      rejected: all.filter((c) => c.status === 'rejected').length,
      snoozed: all.filter((c) => c.status === 'snoozed').length,
      failed: all.filter((c) => c.status === 'failed').length,
      handledExternally: all.filter((c) => c.status === 'handled_externally').length,
      superseded: all.filter((c) => c.status === 'superseded').length,
      total: all.length
    };
  });

  /**
   * All-time count of terminal (archived) cards. Uses the `count()` aggregate
   * (`_archivedTotal`) but never reports fewer than what's actually loaded into
   * `_cards`, so the badge stays correct before the aggregate resolves / if it
   * fails, and in legacy (full-load) mode.
   */
  readonly archivedTotal = computed(() => {
    const loaded = this._cards().filter((c) => TERMINAL_CARD_STATUSES.has(c.status)).length;
    return Math.max(this._archivedTotal(), loaded);
  });

  /**
   * Cards that are awaiting owner attention. Includes both `pending`
   * (manual approve) and `scheduled_auto` (will execute unless cancelled).
   * Without `scheduled_auto`, mail-bridge cards (payment reminders,
   * indexations, etc.) are produced by the backend but never surface on
   * the dashboard — the symptom that broke the contract→cockpit flow.
   */
  readonly pendingCards = computed(() =>
    this._cards()
      .filter((c) => c.status === 'pending' || c.status === 'scheduled_auto')
      .sort((a, b) => this.priorityWeight(b) - this.priorityWeight(a))
  );

  /**
   * Auto-scheduled cards specifically — the ones that will execute on
   * a timer unless the owner cancels. Sorted by when they auto-fire so
   * the soonest-expiring ones appear first.
   */
  readonly scheduledCards = computed(() =>
    this._cards()
      .filter((c) => c.status === 'scheduled_auto')
      .sort((a, b) => {
        const at = a.resolution?.autoExecuteAt ?? '';
        const bt = b.resolution?.autoExecuteAt ?? '';
        return at.localeCompare(bt);
      })
  );

  /**
   * Cards parked on a WAIT_INBOUND step (`waiting_external`) — e.g. after a
   * provider was dispatched and we're holding for their reply. Excluded from
   * `pendingCards`, so without a dedicated surface they'd vanish from the
   * dashboard entirely. Newest first.
   */
  readonly waitingCards = computed(() =>
    this._cards()
      .filter((c) => c.status === 'waiting_external')
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  );

  /**
   * Snoozed cards (`status === 'snoozed'`). Sorted by `resolution.snoozeUntil`
   * ascending so the soonest-waking card surfaces first; cards missing a wake
   * date sort last. Surfaced in the dashboard's bottom "En attente / en veille"
   * zone — they're excluded from `pendingCards`, so without this they vanish
   * from the cockpit entirely.
   */
  readonly snoozedCards = computed(() =>
    this._cards()
      .filter((c) => c.status === 'snoozed')
      .sort((a, b) => {
        // ISO dates start with the year, so '9999' sorts after any real
        // wake date — cards without a snoozeUntil fall to the bottom.
        const at = a.resolution?.snoozeUntil ?? '9999';
        const bt = b.resolution?.snoozeUntil ?? '9999';
        return at.localeCompare(bt);
      })
  );

  /**
   * Terminally-resolved cards — the cockpit's archive. Includes the truly
   * "done" statuses plus `failed` so a failed action never silently
   * disappears (retry stays available from the detail drawer). Sorted by
   * `resolution.resolvedAt` descending (most recent first); cards lacking a
   * resolution timestamp fall back to `createdAt`.
   */
  readonly archivedCards = computed(() => {
    const sortKey = (c: ActionCard): string => c.resolution?.resolvedAt ?? c.createdAt;
    return this._cards()
      .filter((c) => TERMINAL_CARD_STATUSES.has(c.status))
      .sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
  });

  /**
   * Cards with an in-flight multi-step workflow (`workflow.status ===
   * 'active'`) — whether the owner is mid-step (`pending`) or parked
   * (`waiting_external`). The "active workflows" indicator (nav badge,
   * dashboard) reads this so an in-progress process is visible from
   * anywhere, not just by scrolling the cockpit feed. Newest first.
   *
   * Terminal-status cards are excluded even when their `workflow.status` is
   * still `'active'`: that's stale data from a resolution path that couldn't
   * reconcile the workflow (see `TERMINAL_CARD_STATUSES`). Counting them made
   * the KPI claim an active workflow with no matching card in the feed.
   */
  readonly activeWorkflowCards = computed(() =>
    this._cards()
      .filter((c) => c.workflow?.status === 'active' && !TERMINAL_CARD_STATUSES.has(c.status))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  );

  readonly urgentCards = computed(() =>
    this.pendingCards().filter(
      (c) => c.classification.priority === 'emergency' || c.classification.priority === 'high'
    )
  );

  readonly cardsByCategory = computed(() => {
    const pending = this.pendingCards();
    const moneyIntents: CardIntent[] = ['PAYMENT', 'REMINDER', 'INDEXATION'];
    const repairIntents: CardIntent[] = ['MAINTENANCE'];
    const contractIntents: CardIntent[] = ['LEGAL', 'RENEWAL'];
    return {
      money: pending.filter((c) => moneyIntents.includes(c.classification.intent)),
      repairs: pending.filter((c) => repairIntents.includes(c.classification.intent)),
      contracts: pending.filter((c) => contractIntents.includes(c.classification.intent)),
      communications: pending.filter(
        (c) => c.classification.intent === 'INFO' && c.proposal.actionType !== 'NO_ACTION'
      )
    };
  });

  /**
   * Long-lived "in-progress" cards bucketed by business type (interventions /
   * indexations / notices / payments) with a per-status sub-count. Powers the
   * dashboard's "En cours" tiles: instead of hundreds of parked rows, a 100-
   * property manager sees one tile per family with a count + what needs them.
   * Built off the full card list (not `pendingCards`) so `attention` cards are
   * counted here AND still surface individually in the flux above. Pure helper
   * in `cockpit-group.util.ts`.
   */
  readonly inProgressGroups = computed<readonly CockpitGroup[]>(() =>
    buildInProgressGroups(this._cards())
  );

  /**
   * Nightly rolling-30 aggregate (#256). One document, one listener, and a
   * `null` whenever it is absent — a workspace too young for an honest average
   * has no document at all, and the strip must show nothing rather than a zero.
   */
  private readonly rolling30$: Observable<CockpitRolling30 | null> = toObservable(
    this.workspaceStore.currentWorkspaceId
  ).pipe(
    switchMap((wid) =>
      wid
        ? (this.fs.docData(
            this.fs.doc(this.firestore, `workspaces/${wid}/cockpitStats/rolling30`)
          ) as Observable<CockpitRolling30 | undefined>
          ).pipe(map((d) => d ?? null))
        : of(null)
    )
  );

  readonly rolling30 = toSignal(this.rolling30$, { initialValue: null });

  readonly financialSummary = computed(() => {
    const pending = this.pendingCards();
    const overdue = pending.filter((c) => c.classification.intent === 'REMINDER');
    const upcoming = pending.filter((c) => c.classification.intent === 'PAYMENT');
    const sum = (arr: typeof pending): number =>
      arr.reduce((s, c) => s + (c.extraction.entities.amount?.value ?? 0), 0);
    return {
      overdueAmount: sum(overdue),
      upcomingAmount: sum(upcoming),
      overdueCount: overdue.length,
      upcomingCount: upcoming.length,
      currency: pending[0]?.extraction.entities.amount?.currency ?? 'EUR'
    };
  });

  readonly resolvedToday = computed(() => {
    const today = new Date().toDateString();
    return this._cards().filter(
      (c) =>
        c.resolution?.resolvedAt &&
        new Date(c.resolution.resolvedAt).toDateString() === today
    );
  });

  readonly resolvedTodayBreakdown = computed(() => {
    const resolved = this.resolvedToday();
    return {
      total: resolved.length,
      executed: resolved.filter((c) => c.status === 'executed' || c.status === 'approved').length,
      rejected: resolved.filter((c) => c.status === 'rejected').length,
      snoozed: resolved.filter((c) => c.status === 'snoozed').length
    };
  });

  select(id: string): void {
    this._selectedId.set(id);
  }

  clearSelection(): void {
    this._selectedId.set(null);
  }

  nextPendingAfter(cardId: string): string | null {
    const list = this.pendingCards();
    const rest = list.filter((c) => c.id !== cardId);
    if (rest.length === 0) return null;
    const idx = list.findIndex((c) => c.id === cardId);
    if (idx < 0) return rest[0].id;
    // The card that shifts into the resolved card's slot (or the last one when
    // it was at the end) — i.e. "open the next card".
    return rest[Math.min(idx, rest.length - 1)].id;
  }

  updateFilters(patch: Partial<CockpitFilters>): void {
    this._filters.update((current) => ({ ...current, ...patch }));
  }

  resetFilters(): void {
    this._filters.set({ ...DEFAULT_FILTERS });
  }

  /**
   * Lazily load the archive of terminal cards (most-recent first) for the
   * dashboard's "Archivées" tab / inbox terminal views. Uses the bounded
   * `status in + orderBy createdAt + limit` query (composite index required). No
   * fallback to a full-collection read: a query error (e.g. a missing index) is
   * logged so it surfaces during validation instead of silently scanning the
   * whole collection.
   */
  async loadArchive(): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    const ref = this.fs.collection(this.firestore, `workspaces/${wid}/actionCards`);
    try {
      const q = this.fs.query(
        ref,
        this.fs.where('status', 'in', TERMINAL_CARD_STATUS_ARRAY),
        this.fs.orderBy('createdAt', 'desc'),
        this.fs.limit(ARCHIVE_PAGE_SIZE)
      );
      const snap = await this.fs.getDocs(q);
      this._archiveCards.set(snap.docs.map((d) => normalizeCard({ ...(d.data() as ActionCard), id: d.id })));
      // Only a SUCCESSFUL load counts (#257): a permission-denied during the
      // claims-propagation window must leave the offer standing, not pretend
      // the archive is in memory.
      this._archiveLoaded.set(true);
    } catch (err) {
      // permission-denied during the onboarding/auth-claims-propagation window is
      // expected and self-heals once membership claims land — don't alarm.
      if (isPermissionDeniedError(err)) return;
      console.error('[Cockpit] loadArchive bounded query failed (check the actionCards status+createdAt index):', err);
    }
  }

  /** Refresh the terminal-card count() aggregate that backs `archivedTotal`. */
  private async refreshArchivedTotal(): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    try {
      const ref = this.fs.collection(this.firestore, `workspaces/${wid}/actionCards`);
      const q = this.fs.query(ref, this.fs.where('status', 'in', TERMINAL_CARD_STATUS_ARRAY));
      const snap = await this.fs.getCountFromServer(q);
      this._archivedTotal.set(snap.data().count);
    } catch (err) {
      // Same transient window as loadArchive: the count() aggregate is denied
      // until membership claims propagate post-onboarding. `archivedTotal`
      // falls back to the loaded count, so silence the expected case.
      if (isPermissionDeniedError(err)) return;
      console.error('[Cockpit] refreshArchivedTotal count() failed:', err);
    }
  }

  toggleSource(source: CardSource): void {
    this._filters.update((f) => ({ ...f, sources: this.toggleArray(f.sources, source) }));
  }

  toggleIntent(intent: CardIntent): void {
    this._filters.update((f) => ({ ...f, intents: this.toggleArray(f.intents, intent) }));
  }

  togglePriority(priority: CardPriority): void {
    this._filters.update((f) => ({ ...f, priorities: this.toggleArray(f.priorities, priority) }));
  }

  async approve(id: string): Promise<void> {
    await this.mutate(id, (c) => ({
      status: 'executed',
      resolution: {
        ...(c.resolution ?? {}),
        resolvedBy: this.currentUid(),
        resolvedAt: new Date().toISOString(),
        resultRef: ''
      }
    }));
  }

  async editAndApprove(id: string, patch: { draftBody?: string }): Promise<void> {
    const card = this._cards().find((c) => c.id === id);
    if (!card) return;
    await this.mutate(id, (c) => ({
      proposal: { ...c.proposal, draftBody: patch.draftBody ?? c.proposal.draftBody },
      status: 'executed',
      resolution: {
        ...(c.resolution ?? {}),
        resolvedBy: this.currentUid(),
        resolvedAt: new Date().toISOString(),
        resultRef: ''
      }
    }));
  }

  async reject(id: string, reason: string): Promise<void> {
    await this.mutate(id, (c) => ({
      status: 'rejected',
      resolution: {
        ...(c.resolution ?? {}),
        resolvedBy: this.currentUid(),
        resolvedAt: new Date().toISOString(),
        rejectionReason: reason
      }
    }));
    this.trackCardEvent('cockpit.card_rejected', id);
  }

  async snooze(id: string, untilIso: string): Promise<void> {
    await this.mutate(id, (c) => ({
      status: 'snoozed',
      resolution: {
        ...(c.resolution ?? {}),
        resolvedBy: this.currentUid(),
        resolvedAt: new Date().toISOString(),
        snoozeUntil: untilIso
      }
    }));
  }

  async reopen(id: string): Promise<void> {
    await this.mutate(id, () => ({ status: 'pending', resolution: null }));
  }

  /**
   * Owner-driven "Mettre en attente": park a card in `waiting_external` so it
   * drops to the dashboard's "En attente" zone instead of sitting in the
   * active feed — without resolving it. Frontend writes only `status` (the
   * top-level `wait` field is function-only per the Firestore rules), so a
   * manually-parked card carries no `wait`/`workflow` and never auto-advances;
   * the owner brings it back with `reopen` (Réactiver) or closes it with
   * `markHandledExternally` (Marquer résolu). Auto-parked cards differ in that
   * the backend stamps `card.wait` for inbound/timeout resolution.
   */
  async park(id: string): Promise<void> {
    await this.mutate(id, () => ({ status: 'waiting_external' }));
    this.trackCardEvent('cockpit.card_parked', id);
  }

  async resolveNoLongerRelevant(id: string, reason: string): Promise<void> {
    await this.mutate(id, (c) => ({
      status: 'superseded',
      resolution: {
        ...(c.resolution ?? {}),
        resolvedBy: this.currentUid(),
        resolvedAt: new Date().toISOString(),
        rejectionReason: `no_longer_relevant:${reason}`
      }
    }));
  }

  async markHandledExternally(id: string, note?: string): Promise<void> {
    await this.mutate(id, (c) => {
      const rejectionReason = note?.trim() || c.resolution?.rejectionReason;
      return {
        status: 'handled_externally',
        resolution: {
          ...(c.resolution ?? {}),
          resolvedBy: this.currentUid(),
          resolvedAt: new Date().toISOString(),
          // Only include rejectionReason when it is actually defined. A nested
          // `undefined` makes updateDoc THROW (no ignoreUndefinedProperties on
          // the frontend), which silently no-op'd "Marquer résolu" / "Stop
          // waiting" for the common no-note case.
          ...(rejectionReason ? { rejectionReason } : {})
        }
      };
    });
    this.trackCardEvent('cockpit.card_handled_externally', id);
  }

  /** Best-effort product-analytics event for a card lifecycle action. */
  private trackCardEvent(event: string, cardId: string): void {
    const card = this._cards().find((c) => c.id === cardId);
    this.productAnalytics.track(event, {
      cardId,
      ...(card?.classification?.scenarioId ? { scenarioId: card.classification.scenarioId } : {}),
    });
  }

  async toggleEffect(cardId: string, effectId: string): Promise<void> {
    // Effect-level toggling is purely client-side: Firestore rules reject
    // any write to `proposal.effects` from the frontend, and the backend
    // only consumes the selection at approve time (as `enabledEffectIds`).
    // We therefore flip the flag in the optimistic-patch overlay so the
    // merged `_cards()` view (and the checkbox bound to `ef.enabled`)
    // updates instantly. The override is dropped in `approveWithEffects`.
    const card = this._cards().find((c) => c.id === cardId);
    if (!card) return;
    // Resolve via `getCardEffects` so the effect id matches the set the UI
    // renders (it derives a synthetic effect for legacy single-action cards
    // that lack a `proposal.effects` array).
    const targetEffect = getCardEffects(card).find((e) => e.id === effectId);
    if (!targetEffect) return;
    const nextEnabled = !targetEffect.enabled;
    let persistMap: Record<string, boolean> = {};
    this._optimisticPatches.update((current) => {
      const existing = current[cardId]?.effectsEnabled ?? {};
      persistMap = { ...existing, [effectId]: nextEnabled };
      return {
        ...current,
        [cardId]: {
          ...current[cardId],
          effectsEnabled: persistMap
        }
      };
    });
    // V2.7 — persist so the toggle survives a reload (dropped on approve).
    persistEffectsEnabled(cardId, persistMap);
  }

  async approveWithEffects(
    id: string,
    draftEdits?: Record<string, string>,
    payloadEdits?: Record<string, Record<string, unknown>>,
    options?: ApproveOptions
  ): Promise<ApproveResult> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) throw new Error('NO_WORKSPACE');
    const card = this._cards().find((c) => c.id === id);
    const enabledEffectIds = card?.proposal.effects
      ?.filter((e) => e.enabled)
      .map((e) => e.id);
    const callable = this.fs.httpsCallable<
      {
        workspaceId: string;
        cardId: string;
        enabledEffectIds?: string[];
        draftEdits?: Record<string, string>;
        payloadEdits?: Record<string, Record<string, unknown>>;
        sendMode?: ApproveSendMode;
        chosenNextStepId?: string;
        force?: boolean;
        requestId?: string;
      },
      ApproveResult
    >(this.functions, 'approveActionCard');
    // V2.3 — idempotent retry: one requestId for this user action, re-sent on a
    // transient-error retry so the backend replays the original outcome if the
    // first attempt actually succeeded (never double-executes effects).
    const requestId = newRequestId();
    const payload = {
      workspaceId: wid,
      cardId: id,
      enabledEffectIds,
      draftEdits,
      payloadEdits,
      sendMode: options?.sendMode,
      chosenNextStepId: options?.chosenNextStepId,
      force: options?.force,
      requestId
    };
    let res: { data: ApproveResult };
    try {
      res = await callable(payload);
    } catch (err) {
      if (!isTransientCallableError(err)) throw err;
      // Retry exactly once with the same requestId — safe because the backend
      // dedupes on it. This sits AFTER V1.1's server-side guard (mandatory
      // ordering, plan §7.1), so a duplicate can never re-run effects.
      res = await callable(payload);
    }
    // Drop any per-effect `enabled` overlay now that the selection has been
    // committed to the backend. The backend never echoes per-effect enabled
    // flags, so `remoteMatchesPatch` can't auto-clear an effectsEnabled-only
    // patch — without this the stale overlay would linger on the card.
    this.dropEffectsEnabledPatch(id);
    return res.data;
    // The realtime `actionCards` subscription picks up the server-side
    // status / proposal.effects / events / workflow update; no local mutation.
  }

  async skipStep(id: string): Promise<ApproveResult> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) throw new Error('NO_WORKSPACE');
    const callable = this.fs.httpsCallable<
      {
        workspaceId: string;
        cardId: string;
        enabledEffectIds: string[];
      },
      ApproveResult
    >(this.functions, 'approveActionCard');
    const res = await callable({ workspaceId: wid, cardId: id, enabledEffectIds: [] });
    return res.data;
  }

  async claimAsPending(id: string): Promise<void> {
    await this.reopen(id);
  }

  async updateLinkage(
    cardId: string,
    patch: { contactId?: string | null; propertyId?: string | null; contractId?: string | null; tenantIds?: string[] | null },
    opts?: { refreshRecipient?: boolean }
  ): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    // Optimistic: stamp the overlay immediately so the chip flips before
    // the callable round-trip and Firestore propagation finish. The
    // reconciler effect will drop the overlay once realtime catches up.
    this.applyOptimisticLinkagePatch(cardId, patch);
    const callable = this.fs.httpsCallable<
      {
        workspaceId: string;
        cardId: string;
        patch: { contactId?: string | null; propertyId?: string | null; contractId?: string | null; tenantIds?: string[] | null };
        refreshRecipient?: boolean;
      },
      { ok: true }
    >(this.functions, 'updateCardLinkage');
    try {
      await callable({ workspaceId: wid, cardId, patch, ...(opts?.refreshRecipient ? { refreshRecipient: true } : {}) });
    } catch (err) {
      this.dropOptimisticPatch(cardId);
      throw err;
    }
    // On success: leave the overlay in place. The realtime stream will
    // arrive with the same values shortly, and the reconciler clears the
    // overlay then. This avoids the brief revert-then-reapply flash that
    // would happen if we cleared the overlay synchronously here.
  }

  /**
   * Universal baseline "Vu": stamp `seenAt` + a `VIEWED` event via the
   * `markCardSeen` callable (the direct `updateDoc` path can't write `seenAt` —
   * the security rules only allow `status`/`resolution`/`updatedAt`). The
   * realtime `actionCards` stream delivers the new `seenAt`; no local write.
   */
  async markSeen(id: string): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    const callable = this.fs.httpsCallable<
      { workspaceId: string; cardId: string },
      { ok: true; seenAt: string }
    >(this.functions, 'markCardSeen');
    await callable({ workspaceId: wid, cardId: id });
  }

  /**
   * #248 — hand the card to a workspace member (or clear it with `null`) via
   * the `assignCard` callable. Same reason as `markSeen`: the security rules
   * only allow `status`/`resolution`/`updatedAt` from the client, so `assignee`
   * cannot be written directly. No optimistic patch — the realtime stream
   * delivers the new `assignee` within a tick, and a wrong optimistic name
   * (the callable resolves the display name server-side) would flicker.
   */
  async assign(cardId: string, userId: string | null): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    const callable = this.fs.httpsCallable<
      { workspaceId: string; cardId: string; userId: string | null },
      { ok: true; changed: boolean }
    >(this.functions, 'assignCard');
    await callable({ workspaceId: wid, cardId, userId });
  }

  /**
   * Apply one action to a selection of cards via the `bulkCardAction`
   * callable. Like `markSeen` this writes nothing locally — the realtime
   * `actionCards` stream delivers every new status, so a partially-applied
   * batch converges on the truth instead of on an optimistic guess.
   */
  async bulkAction(
    cardIds: string[],
    action: BulkCardAction,
    payload?: { snoozeUntil?: string; reason?: string }
  ): Promise<BulkCardResult> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) throw new Error('NO_WORKSPACE');
    const callable = this.fs.httpsCallable<
      {
        workspaceId: string;
        cardIds: string[];
        action: BulkCardAction;
        payload?: { snoozeUntil?: string; reason?: string };
      },
      BulkCardResult
    >(this.functions, 'bulkCardAction');
    const res = await callable({ workspaceId: wid, cardIds, action, payload });
    this.productAnalytics.track('cockpit.cards_bulk_action', {
      action,
      requested: cardIds.length,
      ok: res.data.ok.length,
      failed: res.data.failed.length,
    });
    return res.data;
  }

  /**
   * V2.6 — regenerate a mail effect's draft via the `regenerateCardDraft`
   * callable. The realtime `actionCards` stream delivers the new `draftBody`;
   * no local mutation needed.
   */
  async regenerateDraft(
    cardId: string,
    effectId: string,
    feedback?: string
  ): Promise<{ draftBody: string; changed: boolean }> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) throw new Error('NO_WORKSPACE');
    // Routed through the provisioned `aiAgentRouter` (one warm agent instance).
    const callable = this.fs.httpsCallable<
      { method: string; payload: { workspaceId: string; cardId: string; effectId: string; feedback?: string } },
      { ok: true; draftBody: string; changed: boolean }
    >(this.functions, 'aiAgentRouter');
    const res = await callable({
      method: 'regenerateCardDraft',
      payload: { workspaceId: wid, cardId, effectId, feedback },
    });
    return { draftBody: res.data.draftBody, changed: res.data.changed };
  }

  /** Progress-band narrative, through the same warm `aiAgentRouter`. The
   *  realtime stream delivers the persisted `progressSummary`, so the band
   *  re-renders from Firestore rather than from this return value. */
  async summarizeProgress(cardId: string, lang: string, force?: boolean): Promise<SummarizeProgressResult> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) throw new Error('NO_WORKSPACE');
    const callable = this.fs.httpsCallable<AiRouterRequest, SummarizeProgressResponse>(this.functions, 'aiAgentRouter');
    const res = await callable({ method: 'summarizeCardProgress', payload: { workspaceId: wid, cardId, lang, force } });
    this.productAnalytics.track('cockpit.card_progress_summarized', { cached: res.data.cached });
    return { text: res.data.summary.text, cached: res.data.cached };
  }

  private applyOptimisticLinkagePatch(
    cardId: string,
    patch: { contactId?: string | null; propertyId?: string | null; contractId?: string | null; tenantIds?: string[] | null }
  ): void {
    this._optimisticPatches.update((current) => {
      const existing = current[cardId]?.linkage ?? {};
      const next: OptimisticPatch['linkage'] = { ...existing };
      if ('contactId' in patch) next.contactId = patch.contactId ?? null;
      if ('propertyId' in patch) next.propertyId = patch.propertyId ?? null;
      if ('contractId' in patch) next.contractId = patch.contractId ?? null;
      if ('tenantIds' in patch) next.tenantIds = patch.tenantIds ?? null;
      return { ...current, [cardId]: { linkage: next } };
    });
  }

  private dropOptimisticPatch(cardId: string): void {
    this._optimisticPatches.update((current) => {
      if (!(cardId in current)) return current;
      const next = { ...current };
      delete next[cardId];
      return next;
    });
  }

  /**
   * Clear only the per-effect `enabled` overlay for a card, leaving any
   * concurrent linkage overlay in place (that one is reconciled against the
   * realtime stream by `remoteMatchesPatch`). Called after an approve
   * commits the effect selection to the backend.
   */
  private dropEffectsEnabledPatch(cardId: string): void {
    this._optimisticPatches.update((current) => {
      const entry = current[cardId];
      if (!entry?.effectsEnabled) return current;
      const next = { ...current };
      if (entry.linkage) {
        next[cardId] = { linkage: entry.linkage };
      } else {
        delete next[cardId];
      }
      return next;
    });
    // V2.7 — the selection is committed server-side; drop the persisted copy.
    clearPersistedEffectsEnabled(cardId);
  }

  async promoteContact(
    contactId: string,
    target: { tenantId?: string; serviceProviderId?: string }
  ): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    const callable = this.fs.httpsCallable<
      {
        workspaceId: string;
        contactId: string;
        tenantId?: string;
        serviceProviderId?: string;
      },
      { ok: true }
    >(this.functions, 'promoteCockpitContact');
    await callable({
      workspaceId: wid,
      contactId,
      tenantId: target.tenantId,
      serviceProviderId: target.serviceProviderId
    });
  }

  async updateContactRole(contactId: string, role: ActionCard['senderRole']): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    const callable = this.fs.httpsCallable<
      { workspaceId: string; contactId: string; role: ActionCard['senderRole'] },
      { ok: true }
    >(this.functions, 'updateCockpitContact');
    await callable({ workspaceId: wid, contactId, role });
  }

  async captureSenderAssociation(
    contactId: string,
    input: {
      role?: ActionCard['senderRole'];
      propertyTag?: { propertyId: string; role: ActionCard['senderRole']; roleSubtype?: string };
    }
  ): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) return;
    const callable = this.fs.httpsCallable<
      {
        workspaceId: string;
        contactId: string;
        role?: ActionCard['senderRole'];
        propertyTag?: { propertyId: string; role: ActionCard['senderRole']; roleSubtype?: string };
      },
      { ok: true }
    >(this.functions, 'updateCockpitContact');
    await callable({
      workspaceId: wid,
      contactId,
      ...(input.role ? { role: input.role } : {}),
      ...(input.propertyTag ? { propertyTag: input.propertyTag } : {})
    });
  }

  /**
   * #374 — the only client write on `cockpitTasks`. Three fields, matching the
   * `hasOnly(['status', 'closedAt', 'closedBy'])` guard in `firestore.rules`
   * exactly; anything else here would be rejected by the server, not silently
   * dropped, so the list stays whatever it was and the caller shows the error.
   *
   * `closedBy` MUST be the signed-in uid — the rule compares it to
   * `request.auth.uid`, which is `auth.currentUser.uid`, not the profile
   * document. There is no optimistic patch to write: the realtime query is
   * `where('status','==','open')`, so the SDK drops the row locally the moment
   * the write is issued and restores it if the server refuses.
   */
  async closeTask(taskId: string, outcome: CockpitTaskOutcome): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    const uid = this.auth.currentUser?.uid ?? null;
    if (!wid || !uid) throw new Error('cockpit/close-task: no workspace or no signed-in user');
    const ref = this.fs.doc(this.firestore, `workspaces/${wid}/cockpitTasks/${taskId}`);
    await this.fs.updateDoc(ref, {
      status: outcome,
      closedAt: this.fs.serverTimestamp(),
      closedBy: uid
    });
  }

  /**
   * Undo — the same three fields, back to `open` with the signature cleared.
   * `null`, never `undefined`: frontend writes run without
   * `ignoreUndefinedProperties`, and the rule reads `closedBy == null`.
   */
  async reopenTask(taskId: string): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    if (!wid) throw new Error('cockpit/reopen-task: no workspace');
    const ref = this.fs.doc(this.firestore, `workspaces/${wid}/cockpitTasks/${taskId}`);
    await this.fs.updateDoc(ref, {
      status: 'open',
      closedAt: null,
      closedBy: null
    });
  }

  private async mutate(id: string, patchFn: (c: ActionCard) => Partial<ActionCard>): Promise<void> {
    const wid = this.workspaceStore.currentWorkspaceId();
    const card = this._cards().find((c) => c.id === id);
    if (!wid || !card) return;
    const patch = patchFn(card);
    const ref = this.fs.doc(this.firestore, `workspaces/${wid}/actionCards/${id}`);
    // Rules only allow `status` and `resolution` to change from the frontend;
    // anything else in the patch is dropped by the filter below.
    const allowed: Record<string, unknown> = {
      updatedAt: this.fs.serverTimestamp()
    };
    if ('status' in patch) allowed['status'] = patch.status;
    if ('resolution' in patch) allowed['resolution'] = patch.resolution;
    await this.fs.updateDoc(ref, allowed);
    // A resolve / reopen flips a card in or out of the terminal set → refresh
    // the count() aggregate that backs the "Archivées" badge.
    void this.refreshArchivedTotal();
  }

  private matchesView(card: ActionCard, view: CockpitFilters['savedView']): boolean {
    switch (view) {
      case 'snoozed':
        return card.status === 'snoozed';
      case 'executed':
        return card.status === 'executed' || card.status === 'approved';
      case 'rejected':
        return card.status === 'rejected';
      case 'failed':
        return card.status === 'failed';
      case 'default':
      default:
        return card.status !== 'snoozed';
    }
  }

  /** #257 — one implementation, shared with the demo store and the feed. */
  private matchesSearch(card: ActionCard, search: string): boolean {
    return matchesCardSearch(card, search);
  }

  private toggleArray<T>(arr: T[], value: T): T[] {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  }

  private priorityWeight(c: ActionCard): number {
    switch (c.classification.priority) {
      case 'emergency':
        return 4;
      case 'high':
        return 3;
      case 'medium':
        return 2;
      case 'low':
      default:
        return 1;
    }
  }

  private currentUid(): string {
    return this.auth.currentUser?.uid ?? 'unknown';
  }

  constructor() {
    // Keep the archived-total count() aggregate fresh: refresh when the
    // workspace becomes available (bounded mode reads it for the "Archivées"
    // tab badge / "N de plus").
    effect(() => {
      const wid = this.workspaceStore.currentWorkspaceId();
      if (wid) void this.refreshArchivedTotal();
    });

    // Drop overlay entries the moment the realtime stream agrees with
    // them. Avoids a forever-growing patch map and ensures server-side
    // corrections (e.g. classification.propertyLabel rewrite) flow back
    // into the UI once the optimistic intent is fulfilled.
    effect(() => {
      const remote = this._remoteCards();
      const patches = this._optimisticPatches();
      const ids = Object.keys(patches);
      if (ids.length === 0) return;
      let changed = false;
      const next = { ...patches };
      for (const id of ids) {
        const card = remote.find((c) => c.id === id);
        if (!card) continue;
        if (remoteMatchesPatch(card, patches[id])) {
          delete next[id];
          changed = true;
        }
      }
      if (changed) this._optimisticPatches.set(next);
    });
  }
}

export interface OptimisticPatch {
  linkage?: {
    contactId?: string | null;
    propertyId?: string | null;
    contractId?: string | null;
    /** The owner's tenant pick, in order; `null` = back to the lease's. */
    tenantIds?: string[] | null;
  };
  /**
   * Per-effect `enabled` overrides keyed by effect id. Set by
   * `toggleEffect` when the owner checks/unchecks an effect in the
   * expanded per-effect checklist. The backend never echoes these
   * (the `enabled` flag only travels to the server as `enabledEffectIds`
   * on approve), so the overlay stays sticky until the card is approved —
   * `approveWithEffects` drops it once the callable resolves.
   */
  effectsEnabled?: Record<string, boolean>;
}

export function applyOptimisticPatch(card: ActionCard, patch: OptimisticPatch): ActionCard {
  let next = card;
  if (patch.effectsEnabled) {
    const overrides = patch.effectsEnabled;
    const applyOverrides = (effects: ProposedEffect[] | undefined): ProposedEffect[] =>
      (effects ?? []).map((e) => (e.id in overrides ? { ...e, enabled: overrides[e.id] } : e));
    next = {
      ...next,
      proposal: { ...next.proposal, effects: applyOverrides(next.proposal.effects) }
    };
    // The workflow keeps its OWN copy of the effects, and the step host reads
    // that copy — `isParkedActionStep(step)` looks at `step.effects`, not at
    // `proposal.effects`. Patching only the proposal left a re-enabled effect
    // still parked on its step: no Execute, no Skip, nothing to click (#254).
    if (next.workflow) {
      next = {
        ...next,
        workflow: {
          ...next.workflow,
          steps: next.workflow.steps.map((s) => ({ ...s, effects: applyOverrides(s.effects) }))
        }
      };
    }
  }
  if (!patch.linkage) return next;
  const linkage: CardLinkage = { ...(next.linkage ?? { resolvedAt: new Date().toISOString() }) };
  const classification = { ...next.classification };
  const stamp = (id: string, prev?: LinkageRef): LinkageRef => ({
    id,
    confidence: prev?.id === id ? prev.confidence : 1,
    source: prev?.id === id ? prev.source : 'owner_manual',
    label: prev?.id === id ? prev.label : id
  });
  if ('contactId' in patch.linkage) {
    const v = patch.linkage.contactId;
    if (v === null) linkage.contact = undefined;
    else if (typeof v === 'string') linkage.contact = stamp(v, linkage.contact);
  }
  if ('propertyId' in patch.linkage) {
    const v = patch.linkage.propertyId;
    if (v === null) {
      linkage.property = undefined;
      classification.propertyId = undefined;
      classification.propertyLabel = undefined;
    } else if (typeof v === 'string') {
      linkage.property = stamp(v, linkage.property);
      classification.propertyId = v;
    }
  }
  if ('contractId' in patch.linkage) {
    const v = patch.linkage.contractId;
    if (v === null) {
      linkage.contract = undefined;
      classification.contractId = undefined;
    } else if (typeof v === 'string') {
      linkage.contract = stamp(v, linkage.contract);
      classification.contractId = v;
    }
  }
  if ('tenantIds' in patch.linkage) {
    const ids = patch.linkage.tenantIds ?? [];
    const known = new Map((linkage.tenants ?? []).map((t) => [t.id, t]));
    linkage.tenants = ids.length > 0 ? ids.map((id) => stamp(id, known.get(id))) : undefined;
  } else if ('contractId' in patch.linkage && (next.linkage?.contract?.id ?? null) !== (linkage.contract?.id ?? null)) {
    // Same rule as the backend's `mergeTenants`: a new lease drops the pick.
    linkage.tenants = undefined;
  }
  return { ...next, linkage, classification };
}

/**
 * True iff the remote card already reflects every field set by the patch.
 * `null` patches require the corresponding linkage to be cleared on the
 * remote; string patches require ids to match.
 *
 * A patch carrying a per-effect `effectsEnabled` override can never "match"
 * the remote: the backend doesn't echo per-effect enabled flags (the
 * selection only travels to the server as `enabledEffectIds` on approve),
 * so this returns `false` to keep the overlay sticky. It is cleared
 * explicitly by `dropEffectsEnabledPatch` once the card is approved.
 */
export function remoteMatchesPatch(card: ActionCard, patch: OptimisticPatch): boolean {
  if (patch.effectsEnabled && Object.keys(patch.effectsEnabled).length > 0) return false;
  const lp = patch.linkage;
  if (!lp) return true;
  if ('contactId' in lp) {
    const remoteId = card.linkage?.contact?.id ?? null;
    if ((lp.contactId ?? null) !== remoteId) return false;
  }
  if ('propertyId' in lp) {
    const remoteId = card.linkage?.property?.id ?? card.classification.propertyId ?? null;
    if ((lp.propertyId ?? null) !== remoteId) return false;
  }
  if ('contractId' in lp) {
    const remoteId = card.linkage?.contract?.id ?? card.classification.contractId ?? null;
    if ((lp.contractId ?? null) !== remoteId) return false;
  }
  if ('tenantIds' in lp) {
    const remote = (card.linkage?.tenants ?? []).map((t) => t.id).join(',');
    if ((lp.tenantIds ?? []).join(',') !== remote) return false;
  }
  return true;
}

/**
 * Guarantee the nested shapes the cockpit UI dereferences without optional
 * chaining (`card.extraction.entities`, `card.proposal.effects`,
 * `card.classification.priority`, `card.language.source`…). A single
 * Firestore doc that was written missing one of these (legacy / internal-event
 * / partial scenario build) otherwise throws during render and takes the
 * whole feed + dashboard down. Cheap shallow guard; only fills gaps.
 */
function normalizeCard(card: ActionCard): ActionCard {
  if (!card) return card;
  const extraction = card.extraction ?? ({ summary: '', entities: {} } as ActionCard['extraction']);
  if (!extraction.entities) extraction.entities = {} as CardEntities;
  const proposal = card.proposal ?? ({ actionType: 'NO_ACTION', trustTier: 'low', nextStep: '' } as ActionCard['proposal']);
  const classification = card.classification ?? ({ intent: 'INFO', priority: 'medium', confidence: 0 } as ActionCard['classification']);
  if (
    extraction === card.extraction &&
    proposal === card.proposal &&
    classification === card.classification &&
    card.events
  ) {
    return card;
  }
  return { ...card, extraction, proposal, classification, events: card.events ?? [] };
}

/* -------------------------------------------------------------------------- */
/* V2.3 — idempotent-approve helpers                                          */
/* -------------------------------------------------------------------------- */

/** Generate a per-action idempotency key. Browser crypto with a safe fallback. */
function newRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * True for the transient callable errors worth one automatic retry. The
 * Firebase callable surfaces a `FirebaseError` whose `code` is namespaced
 * (`functions/deadline-exceeded`), so we match on a suffix.
 */
function isTransientCallableError(err: unknown): boolean {
  const code = (err as { code?: string })?.code ?? '';
  return code.includes('deadline-exceeded') || code.includes('unavailable');
}
